import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useApiClient } from "../../providers/AuthProvider";
import { useTenant } from "../../providers/TenantProvider";

// --- Types ---

export type PermissionGroupLabel = "tenant" | "state" | "site" | "tag";

export type PermissionGroup = {
  id: string;
  name: string;
  label: PermissionGroupLabel;
  parentId: string | null;
  tenantId: string;
  userCount: number;
};

export type GroupUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  disabled: boolean;
  roleName: string;
  roleDisplayName: string;
};

type GroupsContextType = {
  // Data
  groups: PermissionGroup[];
  loading: boolean;
  error: string | null;

  // Selection
  selectedGroupId: string | null;
  selectGroup: (id: string | null) => void;

  // CRUD operations
  fetchGroups: () => Promise<void>;
  createGroup: (data: CreateGroupData) => Promise<PermissionGroup>;
  updateGroup: (id: string, data: UpdateGroupData) => Promise<PermissionGroup>;
  deleteGroup: (id: string) => Promise<void>;

  // Helpers
  getGroupById: (id: string) => PermissionGroup | undefined;
  getChildGroups: (parentId: string | null) => PermissionGroup[];
  getRootGroups: () => PermissionGroup[];
  getUsersInGroup: (groupId: string) => Promise<GroupUser[]>;
};

type CreateGroupData = {
  name: string;
  label: PermissionGroupLabel;
  parentId: string | null;
};

type UpdateGroupData = {
  name?: string;
  label?: PermissionGroupLabel;
  parentId?: string | null;
};

const GroupsContext = createContext<GroupsContextType | null>(null);

// --- Provider ---

type Props = {
  children: React.ReactNode;
};

export const GroupsProvider = ({ children }: Props) => {
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // --- Fetch groups ---

  const fetchGroups = useCallback(async () => {
    if (!currentTenantId) {
      setGroups([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId: currentTenantId });
      const data = await apiClient.get<{ groups: PermissionGroup[] }>(`/api/groups?${params}`);
      setGroups(data.groups);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiClient, currentTenantId]);

  // --- Create group ---

  const createGroup = useCallback(
    async (data: CreateGroupData): Promise<PermissionGroup> => {
      const result = await apiClient.post<{ group: PermissionGroup }>("/api/groups", data);
      setGroups((prev) => [...prev, result.group]);
      return result.group;
    },
    [apiClient],
  );

  // --- Update group ---

  const updateGroup = useCallback(
    async (id: string, data: UpdateGroupData): Promise<PermissionGroup> => {
      const result = await apiClient.put<{ group: PermissionGroup }>(`/api/groups/${id}`, data);
      setGroups((prev) => prev.map((g) => (g.id === id ? result.group : g)));
      return result.group;
    },
    [apiClient],
  );

  // --- Delete group ---

  const deleteGroup = useCallback(
    async (id: string): Promise<void> => {
      await apiClient.delete(`/api/groups/${id}`);
      setGroups((prev) => prev.filter((g) => g.id !== id));
      setSelectedGroupId((current) => (current === id ? null : current));
    },
    [apiClient],
  );

  // --- Selection ---

  const selectGroup = useCallback((id: string | null) => {
    setSelectedGroupId(id);
  }, []);

  // --- Helpers ---

  const getGroupById = useCallback(
    (id: string): PermissionGroup | undefined => {
      return groups.find((g) => g.id === id);
    },
    [groups],
  );

  const getChildGroups = useCallback(
    (parentId: string | null): PermissionGroup[] => {
      return groups.filter((g) => g.parentId === parentId);
    },
    [groups],
  );

  const getRootGroups = useCallback((): PermissionGroup[] => {
    // Root groups are tenants (no parent) or groups whose parent is not in our accessible groups
    const groupIds = new Set(groups.map((g) => g.id));
    return groups.filter((g) => g.parentId === null || !groupIds.has(g.parentId));
  }, [groups]);

  const getUsersInGroup = useCallback(
    async (groupId: string): Promise<GroupUser[]> => {
      const data = await apiClient.get<{ users: GroupUser[] }>(`/api/groups/${groupId}/users`);
      return data.users;
    },
    [apiClient],
  );

  // --- Load on mount ---

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // --- Context value ---

  const value: GroupsContextType = {
    groups,
    loading,
    error,
    selectedGroupId,
    selectGroup,
    fetchGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    getGroupById,
    getChildGroups,
    getRootGroups,
    getUsersInGroup,
  };

  return <GroupsContext.Provider value={value}>{children}</GroupsContext.Provider>;
};

// --- Hook ---

export const useGroups = (): GroupsContextType => {
  const context = useContext(GroupsContext);
  if (!context) {
    throw new Error("useGroups must be used within a GroupsProvider");
  }
  return context;
};
